/*
 * dcl-silca-cart.js  (rev 6)
 *
 * Silca gift card threshold, confirmed at the cart.
 *
 * CONTRACT WITH SHOPIFY FLOW. rev 6, 2026-10-01: the threshold moved from $125
 * measured AFTER the 15% code to $150 measured at shelf price BEFORE any code
 * discount. The workflow "Silca, $20 card at $150 spend" computes its subtotal
 * from each Silca line's originalUnitPriceSet times quantity (shelf price,
 * before discounts). The equivalent in cart.js is original_line_price. Clearance items count at their
 * marked price, as before. If the Flow threshold or field changes, change
 * thresholdCents and silcaSubtotal() here to match. If these two drift apart,
 * the cart promises a card the Flow will not issue.
 *
 * Loaded on every page, not just the cart template, because the cart drawer can
 * open anywhere. Exits immediately when no Silca product is in the cart.
 *
 * Google Merchant Center requires that receipt of the promotion is confirmed at
 * cart or checkout. Checkout is not editable without Shopify Plus, so this file
 * is that confirmation. Removing it puts the promotion at risk of disapproval.
 */
(function () {
  'use strict';

  var CFG = {
    vendor: 'Silca',
    thresholdCents: 15000,
    cardValue: '$20',
    termsUrl: '/pages/silca-gift-card-terms',
    nodeId: 'dcl-silca-cart-note',
    /*
     * TWO DIFFERENT FOOTERS. The /cart page uses .cart__footer-wrapper. The cart
     * DRAWER has no such element: sections/cart-drawer.liquid builds
     * .drawer__footer.cart-drawer__footer containing .drawer__footer-head and
     * .drawer__footer-body. Read that file before changing these.
     *
     * NEVER fall back to the checkout button's parent. That parent is
     * form.drawer__footer-buttons, a flex-wrap row holding View cart and
     * Checkout, so a node inserted there becomes a flex sibling and crushes the
     * Checkout button. That is exactly what rev 1 did.
     */
    mountSelectors: ['.cart__footer-wrapper', '.cart-drawer__footer'],
    fallbackSelector: '.drawer__footer-body'
  };

  var pending = false;

  function money(cents) {
    var dollars = cents / 100;
    if (dollars % 1 === 0) return '$' + dollars.toFixed(0);
    return '$' + dollars.toFixed(2);
  }

  /*
   * original_line_price is the line total at shelf price, before any discount
   * code. rev 6 measures the $150 here so applying DC-Silca26 does not move the
   * shopper backwards on the progress line. Falls back to line_price.
   */
  function silcaSubtotal(cart) {
    var items = (cart && cart.items) || [];
    var total = 0;
    for (var i = 0; i < items.length; i++) {
      var item = items[i];
      if (!item || item.vendor !== CFG.vendor) continue;
      var line = item.original_line_price;
      if (typeof line !== 'number') line = item.line_price;
      if (typeof line === 'number') total += line;
    }
    return total;
  }

  function giftIcon() {
    return (
      '<span class="dcl-sc-mark" aria-hidden="true">' +
      '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" ' +
      'stroke-width="2" stroke-linecap="round" stroke-linejoin="round">' +
      '<polyline points="20 12 20 22 4 22 4 12"></polyline>' +
      '<rect x="2" y="7" width="20" height="5"></rect>' +
      '<line x1="12" y1="22" x2="12" y2="7"></line>' +
      '<path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z"></path>' +
      '<path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z"></path>' +
      '</svg></span>'
    );
  }

  function styles() {
    if (document.getElementById('dcl-sc-style')) return;
    /*
     * Inverted to a solid ink strip on Jake's call 2026-08-29, so the line reads
     * as a callout rather than a footnote.
     *
     * The dollar figures are brand red #e42e0f, set by Jake on 2026-08-29 after
     * seeing #ff8266 live and rejecting it as faded. Consistency with the rest
     * of the site won over the contrast table on his call.
     *
     * For the record: #e42e0f measures 3.89:1 on ink #1a1a1a, under the 4.5:1
     * AA threshold for normal text. The copy is bold, which carries it. If this
     * ever needs to pass AA outright, raise the weight or the size rather than
     * changing the hue, because the hue is the decision.
     *
     * Colours carry !important because the theme's own cart rules reach into
     * this block and would otherwise repaint the text.
     */
    var css =
      '#' + CFG.nodeId + '{display:block;text-align:center;padding:.875rem 1rem;' +
      'background:#1a1a1a!important;color:#fff!important;border-radius:12px;' +
      'margin:0 0 .75rem}' +
      '#' + CFG.nodeId + ' .dcl-sc-row{display:flex;align-items:center;justify-content:center;' +
      'gap:.5rem;flex-wrap:wrap}' +
      '#' + CFG.nodeId + ' .dcl-sc-mark{display:inline-flex;align-items:center;justify-content:center;' +
      'width:26px;height:26px;border-radius:50%;background:#fff!important;color:#1a1a1a!important;' +
      'flex:0 0 26px}' +
      /*
       * font-size:inherit rather than a fixed value, so this line matches the
       * Order note / Estimate Shipping / Coupon row exactly. Those buttons
       * carry no size class and inherit from the same footer parent, so
       * inheriting is what makes them match, and it stays matched if the theme
       * scale ever moves.
       */
      '#' + CFG.nodeId + ' .dcl-sc-copy{font-weight:700;font-size:inherit;line-height:1.35;' +
      'color:#fff!important}' +
      '#' + CFG.nodeId + ' .dcl-sc-em{color:#e42e0f!important}' +
      '#' + CFG.nodeId + ' .dcl-sc-terms{display:inline-block;margin-top:.35rem;font-size:.9em;' +
      'font-weight:400;color:#fff!important;text-decoration:underline;opacity:.85}';
    var tag = document.createElement('style');
    tag.id = 'dcl-sc-style';
    tag.textContent = css;
    document.head.appendChild(tag);
  }

  function copyFor(subtotal) {
    if (subtotal >= CFG.thresholdCents) {
      return (
        'Your <span class="dcl-sc-em">' + CFG.cardValue + '</span> Dialed gift card is ' +
        'emailed once your order is placed.'
      );
    }
    var gap = CFG.thresholdCents - subtotal;
    return (
      'Add <span class="dcl-sc-em">' + money(gap) + '</span> more in Silca to earn a ' +
      '<span class="dcl-sc-em">' + CFG.cardValue + '</span> Dialed gift card.'
    );
  }

  function build(subtotal) {
    var wrap = document.createElement('div');
    wrap.id = CFG.nodeId;
    wrap.innerHTML =
      '<div class="dcl-sc-row">' + giftIcon() +
      '<span class="dcl-sc-copy">' + copyFor(subtotal) + '</span></div>' +
      '<a class="dcl-sc-terms" href="' + CFG.termsUrl + '">Terms</a>';
    return wrap;
  }

  function targets() {
    var found = [];
    for (var i = 0; i < CFG.mountSelectors.length; i++) {
      var nodes = document.querySelectorAll(CFG.mountSelectors[i]);
      for (var n = 0; n < nodes.length; n++) found.push(nodes[n]);
    }
    if (!found.length) {
      var fb = document.querySelectorAll(CFG.fallbackSelector);
      for (var f = 0; f < fb.length; f++) found.push(fb[f]);
    }
    return found;
  }

  function clear() {
    var stale = document.querySelectorAll('#' + CFG.nodeId);
    for (var i = 0; i < stale.length; i++) {
      if (stale[i].parentNode) stale[i].parentNode.removeChild(stale[i]);
    }
  }

  function render(subtotal) {
    /*
     * Always tear the old node down first. A quantity change re-renders the cart
     * with a new figure, and a stale number sitting on screen is worse than no
     * number at all.
     */
    clear();
    if (subtotal <= 0) return;
    styles();
    var mounts = targets();
    for (var i = 0; i < mounts.length; i++) {
      var host = mounts[i];
      if (host.querySelector('#' + CFG.nodeId)) continue;
      host.insertBefore(build(subtotal), host.firstChild);
    }
  }

  function refresh() {
    if (pending) return;
    pending = true;
    fetch('/cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (cart) {
        pending = false;
        if (!cart) return;
        render(silcaSubtotal(cart));
      })
      .catch(function () {
        pending = false;
      });
  }

  function watchDrawer() {
    if (!window.MutationObserver) return;
    var seen = false;
    var obs = new MutationObserver(function () {
      /* Same selector problem as the mount target: the drawer footer is
         .cart-drawer__footer, not .cart__footer-wrapper. */
      var drawer = document.querySelector('.cart-drawer__footer, .cart__footer-wrapper');
      var open = !!drawer;
      if (open && !seen) refresh();
      seen = open;
    });
    obs.observe(document.body, { childList: true, subtree: true });
  }

  function watchAdds() {
    document.addEventListener(
      'click',
      function (e) {
        var el = e.target && e.target.closest
          ? e.target.closest('[name="add"], .product-form__submit, [data-add-to-cart]')
          : null;
        if (el) setTimeout(refresh, 800);
      },
      true
    );
  }

  function start() {
    refresh();
    watchDrawer();
    watchAdds();
    document.addEventListener('cart:refresh', refresh);
    document.addEventListener('cart:updated', refresh);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
